import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, deleteDoc, doc, updateDoc, where, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';

interface Property {
  id: string;
  list_number?: string;
  titulo?: string;
  precio_actual_usd?: number;
  direccion?: any;
  agencia_info?: any;
  agente_listador?: string;
  asesor_id?: string[];
  asociado_id?: string[];
  inmobiliaria_id?: string;
}

interface DuplicateGroup {
  list_number: string;
  count: number;
  properties: Property[];
}

interface Agency {
  id: string;
  nombre: string;
  inmobiliaria_id: string;
}

interface Agent {
  id: string;
  nombre: string;
  tipo: 'asesor' | 'asociado';
  asesor_id?: string;
  asociado_id?: string;
  inmobiliaria_id?: string[];
}

const PropertyCleanupManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [orphanProperties, setOrphanProperties] = useState<Property[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [selectedDuplicates, setSelectedDuplicates] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'duplicates' | 'orphans'>('duplicates');

  // Cargar datos iniciales
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Cargar propiedades
      const propsSnapshot = await getDocs(collection(db, 'propiedades'));
      const propsData = propsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];
      setProperties(propsData);

      // Identificar duplicados
      const duplicatesMap = new Map<string, Property[]>();
      propsData.forEach(prop => {
        if (prop.list_number) {
          if (!duplicatesMap.has(prop.list_number)) {
            duplicatesMap.set(prop.list_number, []);
          }
          duplicatesMap.get(prop.list_number)!.push(prop);
        }
      });

      const duplicateGroups: DuplicateGroup[] = [];
      duplicatesMap.forEach((props, list_number) => {
        if (props.length > 1) {
          duplicateGroups.push({
            list_number,
            count: props.length,
            properties: props
          });
        }
      });
      setDuplicates(duplicateGroups.sort((a, b) => b.count - a.count));

      // Identificar propiedades huérfanas (sin agencia)
      const orphans = propsData.filter(prop =>
        !prop.agencia_info?.inmobiliaria_id &&
        !prop.inmobiliaria_id
      );
      setOrphanProperties(orphans);

      // Cargar agencias
      const agenciesSnapshot = await getDocs(collection(db, 'inmobiliaria'));
      const agenciesData = agenciesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Agency[];
      setAgencies(agenciesData);

      // Cargar agentes
      const asesoresSnapshot = await getDocs(collection(db, 'asesor'));
      const asociadosSnapshot = await getDocs(collection(db, 'asociado'));

      const allAgents: Agent[] = [
        ...asesoresSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            tipo: 'asesor' as const,
            nombre: data.nombre || '',
            asesor_id: data.asesor_id,
            asociado_id: data.asociado_id,
            inmobiliaria_id: data.inmobiliaria_id,
            ...data
          };
        }),
        ...asociadosSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            tipo: 'asociado' as const,
            nombre: data.nombre || '',
            asesor_id: data.asesor_id,
            asociado_id: data.asociado_id,
            inmobiliaria_id: data.inmobiliaria_id,
            ...data
          };
        })
      ];
      setAgents(allAgents);

      console.log(`Datos cargados: ${propsData.length} propiedades, ${duplicateGroups.length} grupos de duplicados, ${orphans.length} huérfanas`);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar duplicados seleccionados
  const cleanSelectedDuplicates = async () => {
    if (selectedDuplicates.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Selecciona propiedades duplicadas para eliminar',
        confirmButtonColor: '#6c757d'
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: 'Confirmar eliminación',
      text: `¿Eliminar ${selectedDuplicates.size} propiedades duplicadas?`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: selectedDuplicates.size, message: 'Eliminando duplicados...' });

    let count = 0;
    for (const propId of Array.from(selectedDuplicates)) {
      try {
        await deleteDoc(doc(db, 'propiedades', propId));
        count++;
        setProgress({ current: count, total: selectedDuplicates.size, message: `Eliminando ${count}/${selectedDuplicates.size}` });
      } catch (error) {
        console.error(`Error eliminando propiedad ${propId}:`, error);
      }
    }

    Swal.fire({
      icon: 'success',
      title: '¡Éxito!',
      text: `Se eliminaron ${count} propiedades duplicadas`,
      confirmButtonColor: '#3085d6'
    });
    setSelectedDuplicates(new Set());
    await loadAllData();
    setLoading(false);
  };

  // Auto-limpiar todos los duplicados (mantener solo el primero de cada grupo)
  const autoCleanAllDuplicates = async () => {
    const toDelete: string[] = [];
    duplicates.forEach(group => {
      // Mantener el primero, eliminar el resto
      for (let i = 1; i < group.properties.length; i++) {
        toDelete.push(group.properties[i].id);
      }
    });

    if (toDelete.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin duplicados',
        text: 'No hay duplicados para limpiar',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: 'Auto-limpiar duplicados',
      html: `¿Eliminar automáticamente ${toDelete.length} propiedades duplicadas?<br><br><small>Esto mantendrá solo una copia de cada propiedad.</small>`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: toDelete.length, message: 'Limpiando duplicados automáticamente...' });

    let count = 0;
    for (const propId of toDelete) {
      try {
        await deleteDoc(doc(db, 'propiedades', propId));
        count++;
        setProgress({ current: count, total: toDelete.length, message: `Eliminando ${count}/${toDelete.length}` });
      } catch (error) {
        console.error(`Error eliminando propiedad ${propId}:`, error);
      }
    }

    Swal.fire({
      icon: 'success',
      title: '¡Éxito!',
      text: `Se eliminaron ${count} propiedades duplicadas automáticamente`,
      confirmButtonColor: '#3085d6'
    });
    await loadAllData();
    setLoading(false);
  };

  // Vincular propiedades huérfanas con agencias basándose en agente_listador
  const autoLinkOrphanProperties = async () => {
    if (orphanProperties.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin propiedades huérfanas',
        text: 'No hay propiedades sin agencia para vincular',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: 'Vincular propiedades',
      text: `¿Vincular automáticamente ${orphanProperties.length} propiedades sin agencia?`,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, vincular',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: orphanProperties.length, message: 'Vinculando propiedades...' });

    let linked = 0;
    let failed = 0;

    for (let i = 0; i < orphanProperties.length; i++) {
      const prop = orphanProperties[i];
      setProgress({ current: i + 1, total: orphanProperties.length, message: `Procesando ${i + 1}/${orphanProperties.length}` });

      try {
        let agencyId: string | null = null;
        let agentIds: string[] = [];

        // Buscar agencia basándose en el nombre del agente listador
        if (prop.agente_listador) {
          const agentName = prop.agente_listador.toLowerCase().trim();

          // Buscar agente por nombre
          const matchingAgent = agents.find(agent =>
            agent.nombre?.toLowerCase().includes(agentName) ||
            agentName.includes(agent.nombre?.toLowerCase() || '')
          );

          if (matchingAgent) {
            // Obtener la agencia del agente
            if (matchingAgent.inmobiliaria_id) {
              agencyId = Array.isArray(matchingAgent.inmobiliaria_id)
                ? matchingAgent.inmobiliaria_id[0]
                : matchingAgent.inmobiliaria_id;
            }

            // Agregar el ID del agente
            if (matchingAgent.tipo === 'asesor' && matchingAgent.asesor_id) {
              agentIds.push(matchingAgent.asesor_id);
            } else if (matchingAgent.tipo === 'asociado' && matchingAgent.asociado_id) {
              agentIds.push(matchingAgent.asociado_id);
            }
          }
        }

        // Si encontramos agencia, actualizar la propiedad
        if (agencyId) {
          const updates: any = {
            inmobiliaria_id: agencyId
          };

          if (agentIds.length > 0) {
            const agent = agents.find(a =>
              (a.tipo === 'asesor' && a.asesor_id === agentIds[0]) ||
              (a.tipo === 'asociado' && a.asociado_id === agentIds[0])
            );

            if (agent?.tipo === 'asesor') {
              updates.asesor_id = agentIds;
            } else if (agent?.tipo === 'asociado') {
              updates.asociado_id = agentIds;
            }
          }

          await updateDoc(doc(db, 'propiedades', prop.id), updates);
          linked++;
        } else {
          failed++;
        }

      } catch (error) {
        console.error(`Error vinculando propiedad ${prop.id}:`, error);
        failed++;
      }
    }

    Swal.fire({
      icon: 'success',
      title: '¡Vinculación completada!',
      html: `<strong>${linked}</strong> propiedades vinculadas<br><strong>${failed}</strong> no se pudieron vincular`,
      confirmButtonColor: '#3085d6'
    });
    await loadAllData();
    setLoading(false);
  };

  // Toggle selección de duplicado
  const toggleDuplicateSelection = (propId: string) => {
    const newSelection = new Set(selectedDuplicates);
    if (newSelection.has(propId)) {
      newSelection.delete(propId);
    } else {
      newSelection.add(propId);
    }
    setSelectedDuplicates(newSelection);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            🧹 Limpieza y Vinculación de Propiedades
          </h1>

          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded">
              <div className="text-2xl font-bold text-blue-600">{properties.length}</div>
              <div className="text-sm text-gray-600">Total Propiedades</div>
            </div>
            <div className="bg-red-50 p-4 rounded">
              <div className="text-2xl font-bold text-red-600">
                {duplicates.reduce((sum, g) => sum + g.count - 1, 0)}
              </div>
              <div className="text-sm text-gray-600">Duplicados</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded">
              <div className="text-2xl font-bold text-yellow-600">{orphanProperties.length}</div>
              <div className="text-sm text-gray-600">Sin Agencia</div>
            </div>
            <div className="bg-green-50 p-4 rounded">
              <div className="text-2xl font-bold text-green-600">{agencies.length}</div>
              <div className="text-sm text-gray-600">Agencias</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 mb-6 border-b">
            <button
              onClick={() => setActiveTab('duplicates')}
              className={`pb-2 px-1 ${activeTab === 'duplicates' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
            >
              Limpiar Duplicados ({duplicates.length} grupos)
            </button>
            <button
              onClick={() => setActiveTab('orphans')}
              className={`pb-2 px-1 ${activeTab === 'orphans' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
            >
              Vincular Huérfanas ({orphanProperties.length})
            </button>
          </div>

          {/* Progreso */}
          {loading && progress.total > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded">
              <div className="flex justify-between mb-2">
                <span>{progress.message}</span>
                <span>{progress.current}/{progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Tab de Duplicados */}
          {activeTab === 'duplicates' && (
            <div>
              <div className="flex justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Propiedades Duplicadas</h2>
                  <p className="text-sm text-gray-600">
                    {selectedDuplicates.size} seleccionadas para eliminar
                  </p>
                </div>
                <div className="space-x-2">
                  <button
                    onClick={cleanSelectedDuplicates}
                    disabled={loading || selectedDuplicates.size === 0}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Eliminar Seleccionadas ({selectedDuplicates.size})
                  </button>
                  <button
                    onClick={autoCleanAllDuplicates}
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                  >
                    🚀 Auto-Limpiar Todo
                  </button>
                </div>
              </div>

              {/* Lista de duplicados */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {duplicates.map((group, index) => (
                  <div key={index} className="border rounded p-4 bg-gray-50">
                    <div className="font-semibold mb-2">
                      List Number: {group.list_number} ({group.count} copias)
                    </div>
                    <div className="space-y-2">
                      {group.properties.map((prop, i) => (
                        <div key={prop.id} className="flex items-center space-x-3 p-2 bg-white rounded">
                          <input
                            type="checkbox"
                            checked={selectedDuplicates.has(prop.id)}
                            onChange={() => toggleDuplicateSelection(prop.id)}
                            disabled={i === 0} // No permitir eliminar el primero
                          />
                          <div className="flex-1">
                            <div className="text-sm">
                              {i === 0 && <span className="text-green-600 font-semibold">[MANTENER] </span>}
                              {prop.titulo || 'Sin título'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {prop.id.substring(0, 8)}... |
                              Precio: ${prop.precio_actual_usd?.toLocaleString() || 'N/A'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab de Huérfanas */}
          {activeTab === 'orphans' && (
            <div>
              <div className="flex justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Propiedades Sin Agencia</h2>
                  <p className="text-sm text-gray-600">
                    Se intentará vincular automáticamente basándose en el agente listador
                  </p>
                </div>
                <button
                  onClick={autoLinkOrphanProperties}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  🔗 Vincular Automáticamente
                </button>
              </div>

              {/* Lista de huérfanas */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {orphanProperties.slice(0, 50).map(prop => (
                  <div key={prop.id} className="p-3 bg-gray-50 rounded border">
                    <div className="font-medium">{prop.titulo || 'Sin título'}</div>
                    <div className="text-sm text-gray-600">
                      List #: {prop.list_number || 'N/A'} |
                      Agente: {prop.agente_listador || 'Sin agente'} |
                      ID: {prop.id.substring(0, 8)}...
                    </div>
                  </div>
                ))}
                {orphanProperties.length > 50 && (
                  <div className="text-center text-gray-500 py-2">
                    ... y {orphanProperties.length - 50} más
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCleanupManager;